import React, { useState, useEffect } from 'react';
import supabase from "../config/supabaseClient";
import logo from '../images/logo.jpg';

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const Home = () => {
  const [lastGame, setLastGame] = useState(null);
  const [nextGame, setNextGame] = useState(null);
  const [practicesByDay, setPracticesByDay] = useState({});
  const [stats, setStats] = useState({ goals: 0, concededGoals: 0, points: 0 });
  const [showStatsOnly, setShowStatsOnly] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState('league');
  const [lastGameTeams, setLastGameTeams] = useState('');

  useEffect(() => {
    const fetchGames = async () => {
      const { data: games, error } = await supabase
        .from('game')
        .select(`*, adversaries (Adversarie)`);

      if (error) {
        console.error('Error fetching games:', error);
        return;
      }

      const now = new Date();
      const gamesWithDateTime = games.map(game => {
        const dateTime = new Date(`${game.Date}T${game.Time}`);
        return { ...game, dateTime };
      });

      const pastGames = gamesWithDateTime.filter(game => game.dateTime <= now);
      const futureGames = gamesWithDateTime.filter(game => game.dateTime > now);

      if (pastGames.length > 0) {
        const lastGame = pastGames.reduce((latest, game) =>
          game.dateTime > latest.dateTime ? game : latest
        );
        setLastGame(lastGame);
        fetchLastGameTeams(lastGame.id);
      }

      if (futureGames.length > 0) {
        const nextGame = futureGames.reduce((earliest, game) =>
          game.dateTime < earliest.dateTime ? game : earliest
        );
        setNextGame(nextGame);
      }
    };

    const fetchLastGameTeams = async (gameId) => {
      const { data: results, error } = await supabase
        .from('game_results')
        .select('my_team, adversary_team')
        .eq('game_id', gameId);

      if (error) {
        console.error('Error fetching game results:', error);
        return;
      }

      if (results.length > 0) {
        const { my_team, adversary_team } = results[0];
        setLastGameTeams(`${my_team} - ${adversary_team}`);
      } else {
        setLastGameTeams('No result available');
      }
    };

    const fetchPractices = async () => {
      const { data: practices, error } = await supabase.from('practices').select('*');

      if (error) {
        console.error('Error fetching practices:', error);
        return;
      }

      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      const practicesThisWeek = practices.filter(practice => {
        const practiceDate = new Date(practice.date);
        return practiceDate >= startOfWeek && practiceDate <= endOfWeek;
      });

      const practicesByDay = practicesThisWeek.reduce((acc, practice) => {
        const day = daysOfWeek[new Date(practice.date).getDay()];
        if (!acc[day]) acc[day] = [];
        acc[day].push(practice);
        return acc;
      }, {});

      setPracticesByDay(practicesByDay);
    };

    const fetchStats = async () => {
      const { data: results, error } = await supabase
        .from('game_results')
        .select('my_team, adversary_team, points')
        .eq('competition', selectedCompetition);

      if (error) {
        console.error('Error fetching stats:', error);
        return;
      }

      const goals = results.reduce((sum, r) => sum + r.my_team, 0);
      const concededGoals = results.reduce((sum, r) => sum + r.adversary_team, 0);
      const points = results.reduce((sum, r) => sum + r.points, 0);

      setStats({ goals, concededGoals, points });
    };

    fetchGames();
    fetchPractices();
    fetchStats();
  }, [selectedCompetition]);

  const formatDateTime = (dateTime) =>
    `${dateTime.toLocaleDateString()} ${dateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <>
      <style>{`
        body {
          margin: 0;
          padding: 0;
          font-family: Arial, sans-serif;
        }

        .outer {
          min-height: 100vh;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          background: #f9f9f9;
          padding: 20px;
        }

        .container {
          max-width: 1000px;
          width: 100%;
          background: white;
          padding: 20px;
          border-radius: 10px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.1);
        }

        .header {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          margin-bottom: 20px;
        }

        .image {
          max-width: 300px;
          width: 100%;
          height: auto;
          object-fit: cover;
          margin-bottom: 20px;
          margin-right: 20px;
        }

        .gameInfo {
          flex: 1;
          min-width: 300px;
        }

        .calendar {
          margin-top: 20px;
        }

        .weekGrid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 10px;
        }

        .day {
          border: 1px solid #ccc;
          padding: 10px;
          border-radius: 8px;
        }

        .practice {
          border-bottom: 1px solid #eee;
          padding: 5px 0;
        }

        .statsSection {
          text-align: center;
        }

        .filterGroup {
          margin-bottom: 20px;
        }

        .statsBoxes {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 15px;
        }

        .statsBox {
          border: 1px solid #ccc;
          border-radius: 5px;
          padding: 10px;
          width: 150px;
          text-align: center;
        }

        .button {
          background-color: #007BFF;
          color: #fff;
          border: none;
          border-radius: 5px;
          padding: 10px 20px;
          cursor: pointer;
          margin-top: 10px;
        }
      `}</style>

      <div className="outer">
        <div className="container">
          {showStatsOnly ? (
            <div className="statsSection">
              <h2>Filter Results</h2>
              <div className="filterGroup">
                {['league', 'cup', 'friendly'].map(type => (
                  <label key={type}>
                    <input
                      type="radio"
                      value={type}
                      checked={selectedCompetition === type}
                      onChange={() => setSelectedCompetition(type)}
                    />
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </label>
                ))}
              </div>

              <div className="statsBoxes">
                <div className="statsBox"><h3>Goals</h3><p>{stats.goals}</p></div>
                <div className="statsBox"><h3>Conceded</h3><p>{stats.concededGoals}</p></div>
                <div className="statsBox"><h3>Points</h3><p>{stats.points}</p></div>
              </div>

              <button className="button" onClick={() => setShowStatsOnly(false)}>Show All</button>
            </div>
          ) : (
            <>
              <div className="header">
                <img src={logo} alt="Game Logo" className="image" />
                <div className="gameInfo">
                  <h2>Last Game</h2>
                  {lastGame ? (
                    <div>
                      <p><strong>Journey:</strong> {lastGame.Journey}</p>
                      <p><strong>Date & Time:</strong> {formatDateTime(lastGame.dateTime)}</p>
                      <p><strong>Adversarie:</strong> {lastGame.adversaries?.Adversarie}</p>
                      <p><strong>Teams:</strong> {lastGameTeams}</p>
                    </div>
                  ) : <p>No past games available.</p>}

                  <h2>Next Game</h2>
                  {nextGame ? (
                    <div>
                      <p><strong>Journey:</strong> {nextGame.Journey}</p>
                      <p><strong>Date & Time:</strong> {formatDateTime(nextGame.dateTime)}</p>
                      <p><strong>Adversarie:</strong> {nextGame.adversaries?.Adversarie}</p>
                    </div>
                  ) : <p>No upcoming games available.</p>}
                </div>
              </div>

              <div className="calendar">
                <h2>Practice Schedule for This Week</h2>
                <div className="weekGrid">
                  {daysOfWeek.map(day => (
                    <div key={day} className="day">
                      <h3>{day}</h3>
                      {practicesByDay[day]?.length ? practicesByDay[day].map((p, i) => (
                        <div key={i} className="practice">
                          <p><strong>Date:</strong> {new Date(p.date).toLocaleDateString()}</p>
                          <p><strong>Time:</strong> {p.time}</p>
                          <p><strong>Weather:</strong> {p.weather}</p>
                        </div>
                      )) : <p>No practices</p>}
                    </div>
                  ))}
                </div>
              </div>

              <button className="button" onClick={() => setShowStatsOnly(true)}>Show Stats Only</button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Home;
